"use client";

/**
 * /abilities/[slug] — unified ability detail page.
 *
 * Every ability — system, personal, installed, available — renders through
 * `<InstalledAbilityView>`. The page fetches the catalog detail once so we
 * know the `state` (which gates the Install CTA in the topbar) and so the
 * install modal has its required CatalogAbilityDetail payload without a
 * second fetch. InstalledAbilityView fetches its own ability row + index
 * rows + lazy tab content from supabase.
 *
 * Previously this file routed between `<MarketplaceDetailView>` (for
 * `state === 'available'`) and `<InstalledAbilityView>` (everything else).
 * That split created a jarring before/after-install inconsistency where the
 * same ability looked materially different depending on whether the user
 * had installed it. Collapsed to the tabbed view 2026-05-16.
 *
 * Data flow:
 *   - seed from `getCachedDetail(slug)` in the unified abilities cache so
 *     return-navigation paints instantly;
 *   - background revalidation always runs so stale data refreshes (SWR-ish);
 *   - `loading.tsx` covers the first-paint skeleton.
 *
 * Cache invalidation on install lives inside InstallConfirmModal (calls
 * invalidateAllAbilities()) — both the catalog list cache + this detail cache
 * are cleared together, so the next render sees fresh state.
 */
import { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import { useParams } from "next/navigation";
import type { CatalogAbilityDetail } from "@bash/shared";
import { InstalledAbilityView } from "@/components/abilities/InstalledAbilityView";
import { getCachedDetail, setCachedDetail } from "@/lib/abilities/cache";
import { getCatalogAbility } from "@/lib/api/abilities";
import { useAuth } from "@/contexts/AuthContext";
import { InsideAbilitySkeleton } from "@/components/abilities/skeletons";

export default function AbilityDetailPage() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;
  const { loading: authLoading } = useAuth();

  // Public: anon visitors can view the ability detail (system + published
  // marketplace). Install is gated behind sign-in inside the view.

  const cached = getCachedDetail(slug);
  const [ability, setAbility] = useState<CatalogAbilityDetail | null>(
    cached ?? null
  );
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let alive = true;
    getCatalogAbility(slug)
      .then((d) => {
        if (!alive) return;
        setCachedDetail(slug, d);
        setAbility(d);
        setError(null);
      })
      .catch((e) => {
        if (!alive) return;
        setError(e as Error);
      });
    return () => {
      alive = false;
    };
  }, [slug]);

  // Re-fetch the catalog detail after a member add/remove and push the fresh
  // payload down. A monotonic seq guards against a slow earlier refetch
  // resolving after a newer one (rapid double-mutation) and clobbering with
  // stale data; errors are logged rather than silently swallowed. Defined
  // before the early returns below so the hooks run unconditionally.
  const reloadSeq = useRef(0);
  const reload = useCallback(() => {
    const seq = ++reloadSeq.current;
    getCatalogAbility(slug)
      .then((d) => {
        if (seq !== reloadSeq.current) return;
        setCachedDetail(slug, d);
        setAbility(d);
      })
      .catch((e) => {
        if (seq !== reloadSeq.current) return;
        console.error(
          "Failed to reload ability detail after member change:",
          e
        );
      });
  }, [slug]);

  // Wait for auth to resolve so InstalledAbilityView gets a stable signed-in
  // state (it gates owner-only edit affordances on it). Anon proceeds.
  if (authLoading) {
    return <InsideAbilitySkeleton />;
  }

  if (!ability) {
    if (error) {
      return (
        <>
          <div className="text-3.5 text-status-error">
            Failed to load ability: {error?.message}
          </div>

          <Link
            href="/abilities"
            className="inline-flex text-3.5 text-fg-muted underline"
          >
            Back to Abilities
          </Link>
        </>
      );
    }
    // `loading.tsx` covers the visible state before our first paint.
    return null;
  }

  return (
    <InstalledAbilityView
      slug={slug}
      state={ability.state}
      catalogDetail={ability}
      onMembersChanged={reload}
      onInstalled={() => {
        // Optimistic flip — the cache invalidation in InstallConfirmModal
        // means the next mount re-fetches. Until then, treat the catalog
        // detail as installed so the Install CTA disappears immediately.
        setAbility({ ...ability, installed_by_me: true, state: "installed" });
      }}
      onUninstalled={() => {
        // Optimistic flip — the cache invalidation in InstallConfirmModal
        // means the next mount re-fetches. Until then, treat the catalog
        // detail as installed so the Install CTA disappears immediately.
        setAbility({ ...ability, installed_by_me: false, state: "available" });
      }}
    />
  );
}
